import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Command, Sparkles } from "lucide-react";
import Logo from "@/components/brand/Logo";
import DemoNotice from "@/components/site/DemoNotice";
import { FadeUp, Grain } from "@/components/landing/cinematic";

export default function Footer() {
    return (
        <footer
            className="relative -mt-6 lg:-mt-8 overflow-hidden rounded-t-[32px] lg:rounded-t-[56px] bg-[#151310] text-white"
            data-testid="cta-footer"
        >
            <Grain opacity={0.07} />
            <div className="absolute inset-x-0 top-0 h-[380px] sm:h-[480px] bg-[radial-gradient(ellipse_at_50%_0%,rgba(67,83,228,0.12),transparent_65%)] pointer-events-none" aria-hidden="true" />

            <div className="relative mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
                {/* CTA */}
                <div className="pt-20 sm:pt-28 lg:pt-36 pb-16 sm:pb-20 text-center">
                    <FadeUp>
                        <div className="inline-flex items-center gap-2 rounded-full border border-[#f7f3ec]/12 bg-[#f7f3ec]/[0.04] px-3 sm:px-3.5 py-1.5 font-mono text-[10px] sm:text-[10.5px] uppercase tracking-[0.16em] text-[#c9c2b4]">
                            <Sparkles className="h-3 w-3 text-[#8b96f8]" strokeWidth={2} />
                            v0.1 · Live at Yug Traders
                        </div>
                    </FadeUp>
                    <FadeUp delay={0.1}>
                        <h2 className="mt-6 sm:mt-8 font-display text-[36px] sm:text-6xl lg:text-[76px] font-bold tracking-[-0.035em] text-white leading-[1.04]">
                            Let your business
                            <br />
                            <span className="font-editorial font-medium text-[#a29a8e]">
                                answer its own questions.
                            </span>
                        </h2>
                    </FadeUp>
                    <FadeUp delay={0.2}>
                        <div className="mt-8 sm:mt-11 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center gap-3">
                            <Link to="/login" data-testid="cta-launch-btn">
                                <span className="group inline-flex h-12 w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-[#f7f3ec] pl-6 pr-5 text-[14.5px] font-medium text-[#1c1917] transition-[background-color,transform] duration-200 hover:bg-white hover:-translate-y-0.5">
                                    Launch YugOS
                                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
                                </span>
                            </Link>
                            <a href="#product" data-testid="cta-demo-btn">
                                <span className="inline-flex h-12 w-full sm:w-auto items-center justify-center rounded-full border border-[#f7f3ec]/25 px-6 text-[14.5px] font-medium text-[#f7f3ec] transition-colors duration-200 hover:bg-[#f7f3ec]/10">
                                    Explore the product
                                </span>
                            </a>
                        </div>
                    </FadeUp>
                    <FadeUp delay={0.28}>
                        <div className="mt-6 sm:mt-8 flex items-center justify-center gap-1.5 font-mono text-[10.5px] sm:text-[11px] text-[#6b6459] text-center">
                            <Command className="h-3 w-3 shrink-0" strokeWidth={1.5} />
                            <span>Login is the only entry point · Provisioned by administrators</span>
                        </div>
                    </FadeUp>
                </div>

                <div className="h-px w-full bg-gradient-to-r from-transparent via-[#f7f3ec]/15 to-transparent" />

                <div className="pt-8">
                    <DemoNotice variant="dark" />
                </div>

                {/* Footer meta */}
                <div className="flex flex-col gap-8 py-10 md:flex-row md:items-end md:justify-between">
                    <div>
                        <div className="flex items-center gap-2.5">
                            <Logo size={28} />
                            <span className="font-display text-[15px] font-bold tracking-tight text-white">YugOS</span>
                            <span className="font-mono text-[10px] text-[#6b6459]">v0.1</span>
                        </div>
                        <p className="mt-3 max-w-md text-[13px] leading-relaxed text-[#a29a8e]">
                            Built inside a real wholesale business.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-x-7 gap-y-3 text-[12.5px] text-[#a29a8e]">
                        <Link to="/privacy" className="transition-colors hover:text-white" data-testid="footer-privacy">
                            Privacy Policy
                        </Link>
                        <Link to="/terms" className="transition-colors hover:text-white" data-testid="footer-terms">
                            Terms of Service
                        </Link>
                        <Link to="/contact" className="transition-colors hover:text-white" data-testid="footer-contact">
                            Contact
                        </Link>
                        <Link to="/login" className="transition-colors hover:text-white" data-testid="footer-login">
                            Log in
                        </Link>
                    </div>
                </div>

                <div className="flex flex-col items-start justify-between gap-3 border-t border-[#f7f3ec]/[0.08] pt-6 pb-10 text-[11.5px] text-[#6b6459] sm:flex-row sm:items-center">
                    <div>© {new Date().getFullYear()} YugOS</div>
                    <div className="font-mono">The AI Operating System for wholesale &amp; retail.</div>
                </div>
            </div>

            {/* Watermark wordmark */}
            <div
                aria-hidden="true"
                className="pointer-events-none select-none relative -mb-6 sm:-mb-8 overflow-hidden text-center font-display text-[26vw] sm:text-[22vw] font-bold leading-[0.72] tracking-[-0.05em] text-transparent"
                style={{ WebkitTextStroke: "1px rgba(247,243,236,0.06)" }}
            >
                YugOS
            </div>
        </footer>
    );
}
